import {
  computeSupplementSelectionExtrasTotal,
  formatExtrasSegmentDirectionLabel,
  getFlightSegmentsWithLegs,
  mapRawLdsSupplementsToMockupExtras,
} from "../Components/FlightBookingFlowMockup/adapters/flightOfferAdapter";
import { computeSupplementPriceIls } from "../utils/lds/baggageSupplementPricing";

export function resolveBelvedereCampaignBaggageFeeFlatUsd(baggageFeeFlatUsd) {
  const n = Number(baggageFeeFlatUsd);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** סכום כבודה נבחרת (כולל עמלת אדמין GC BAGGAGE_FEE_FLAT) לפי supplementUnitFaresIls. */
export function computeCampaignBaggageExtrasTotalIls(extras) {
  if (!extras || typeof extras !== "object") return 0;
  const selections = extras.supplementPaxSelections;
  const fares = extras.supplementUnitFaresIls;
  if (!fares || typeof fares !== "object" || !Object.keys(fares).length) return 0;
  return computeSupplementSelectionExtrasTotal(selections, fares);
}

/**
 * Line items for PDF / customer offer — selected paid baggage from campaign extras.
 * @returns {{ lines: Array<{ name: string, priceIls: number, count: number }>, totalIls: number }}
 */
export function buildCampaignBaggageQuoteItems(extras, rawDeal = null) {
  const selections = extras?.supplementPaxSelections;
  const faresIls = extras?.supplementUnitFaresIls;
  if (!selections || typeof selections !== "object") {
    return { lines: [], totalIls: 0 };
  }

  const rows = rawDeal ? mapRawLdsSupplementsToMockupExtras(rawDeal) : [];
  const byKey = new Map(rows.map((r) => [r.supplementKey, r]));
  const numSeg = rawDeal ? getFlightSegmentsWithLegs(rawDeal).length || 1 : 1;
  const lines = [];

  for (const [key, ids] of Object.entries(selections)) {
    if (!Array.isArray(ids) || ids.length === 0) continue;
    const count = ids.length;
    const unit = Number(faresIls?.[key]);
    const lineTotal =
      Number.isFinite(unit) && unit >= 0 ? Math.round(unit * count * 100) / 100 : 0;
    const row = byKey.get(key);
    const dir = row ? formatExtrasSegmentDirectionLabel(row.segmentIndex, numSeg) : "";
    const title = row?.titleLine || row?.description || "כבודה";
    const name = `${dir ? `${dir}: ` : ""}${title} · ${count} נוסעים`.trim();
    lines.push({ name, priceIls: lineTotal, count });
  }

  const totalIls = computeCampaignBaggageExtrasTotalIls(extras);
  return { lines, totalIls };
}

/**
 * Rebuild baggage quote lines from a saved flights.supplement payload (checked items).
 * Used when campaign_selection.baggage_lines is missing (older quotes).
 */
export function buildCampaignBaggageQuoteItemsFromLdsSupplement(supplementPayload) {
  if (!supplementPayload || typeof supplementPayload !== "object") {
    return { lines: [], totalIls: 0 };
  }
  const entries = Array.isArray(supplementPayload)
    ? supplementPayload
    : Object.values(supplementPayload);
  const lines = [];
  let totalIls = 0;
  for (const entry of entries) {
    if (!entry || typeof entry !== "object" || entry.checked !== true) continue;
    const priceIls = Number(entry.ourFare ?? entry.fare ?? 0);
    if (!Number.isFinite(priceIls) || priceIls <= 0) continue;
    const orig = entry.orig?.supp || entry.orig || {};
    const type = String(orig.type || entry.type || "").toUpperCase();
    const kindHe = type === "TROLLEY" ? "טרולי" : "מזוודה";
    const desc = String(orig.desc || orig.description || entry.desc || "").trim();
    const name = desc || kindHe;
    lines.push({ name, priceIls: Math.round(priceIls * 100) / 100, count: 1 });
    totalIls += priceIls;
  }
  return { lines, totalIls: Math.round(totalIls * 100) / 100 };
}

/**
 * מחיר יחידה לתצוגה/LDS — נטו ספק × שער + עמלה קבועה מהאדמין ($).
 */
export function computeCampaignBaggageUnitPriceIls({
  supplierFareUsd,
  currencyRateUsd,
  baggageFeeFlatUsd,
  supplement,
}) {
  return computeSupplementPriceIls({
    fareUsd: supplierFareUsd,
    currencyRateUsd: Number(currencyRateUsd),
    baggageFeeFlatUsd: resolveBelvedereCampaignBaggageFeeFlatUsd(baggageFeeFlatUsd),
    supplement,
  });
}
